import type Quill from 'quill';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';

import { QuillWrapperLoadable } from '#/components/QuillWrapper/QuillWrapperLoadable';
import { useI18n } from '#/i18n/hooks/useI18n';

import {
  BaseRteDismissWarningButton,
  BaseRteHolder,
  BaseRteQuillHolder,
  BaseRteWarningHolder,
  baseRteIntroHeightVar,
  baseRteOutroHeightVar,
  baseRteQuillEditorMinHeightVar,
} from './BaseRte.styled';
import { BaseRteIntroOutro } from './IntroOutro/BaseRteIntroOutro';
import type { BaseRteFeatures } from './types/BaseRteFeatures';
import { baseRteHeaderNumberByHeadingType } from './types/BaseRteHeadingType';
import { baseRteCreateStripHeadersExcept } from './utils/baseRteCreateStripHeadersExcept';

type Props = {
  features: BaseRteFeatures;
  id?: string;
  initialValue: string;
  placeholder?: string;
  intro?: ReactNode;
  outro?: ReactNode;
  onReady?: (editor: Quill) => void;
  onChange?: (newValue: string) => void;
  onPasteText?: (pastedText: string) => void;
  warning?: {
    text: string;
    onDismiss: (event: MouseEvent<HTMLButtonElement>) => void;
  };
  disabled?: boolean;
  qaId: string;
  // TODO(@paprikka): drop this after we've moved fullscreen support to BaseRTE
  isFullscreen?: boolean;
  hasError?: boolean;
  minHeight?: number;
};

/**
 * Base Rich Text Editor component
 *
 * This component is meant to be a base for other RTEs,
 * such as the ListingRte, StaffCmsRte and OrgDashboardRte
 */
export function BaseRte({
  features,
  id,
  initialValue,
  placeholder,
  intro,
  outro,
  onReady,
  onChange,
  onPasteText,
  warning,
  disabled,
  qaId,
  isFullscreen = false,
  hasError = false,
  minHeight = 160,
}: Props) {
  const { i18n } = useI18n();

  const holderRef = useRef<HTMLDivElement>(null);

  const modules = useMemo(() => {
    /**
     * @see https://quilljs.com/docs/modules
     */
    const toolbar = [
      features.header.length > 0 && [
        {
          header: features.header.map((heading) =>
            heading === 'normal'
              ? false
              : baseRteHeaderNumberByHeadingType[heading],
          ),
        },
      ],
      [
        features.bold && 'bold',
        features.italic && 'italic',
        features.underline && 'underline',
        features.strike && 'strike',
        features.link && 'link',
      ].filter(Boolean),
      features.list && [{ list: 'ordered' }, { list: 'bullet' }],
      features.clean && ['clean'],
      features.align && [
        // separate buttons, no justify
        { align: '' },
        { align: 'center' },
        { align: 'right' },
      ],
      features.blockquote && ['blockquote'],
    ];

    return {
      toolbar: toolbar.filter(Boolean),
      fullscreen: features.fullscreen,
      magicUrl: Boolean(features.link),
      newTabLink: features.link === 'true-with-new-tab',
      // Do not enable normalizeList for features.list === 'true-no-normalize'
      normalizeList: features.list === true,
      clipboard:
        features.header.length > 0
          ? {
              matchVisual: false,
              matchers: [
                [
                  1 /* Node.ELEMENT_NODE */,
                  baseRteCreateStripHeadersExcept({ types: features.header }),
                ],
              ],
            }
          : undefined,
      keyboard: {
        bindings: {
          'list autofill':
            features.list === 'true-no-normalize' ? false : undefined,
        },
      },
    };
  }, [
    features.align,
    features.blockquote,
    features.bold,
    features.clean,
    features.header,
    features.italic,
    features.link,
    features.list,
    features.strike,
    features.underline,
    features.fullscreen,
  ]);

  const formats = useMemo(() => {
    /**
     * @see https://quilljs.com/docs/formats
     */
    const formatsAvailable = [
      features.header.length > 0 && 'header',
      features.bold && 'bold',
      features.italic && 'italic',
      features.underline && 'underline',
      features.strike && 'strike',
      features.link && 'link',
      features.list && 'list',
      features.align && 'align',
      features.indent && 'indent',
      features.blockquote && 'blockquote',
    ];

    return formatsAvailable.filter(Boolean);
  }, [
    features.align,
    features.blockquote,
    features.bold,
    features.header,
    features.indent,
    features.italic,
    features.link,
    features.list,
    features.strike,
    features.underline,
  ]);

  const quillRef = useRef<Quill>(null);
  useEffect(() => {
    if (disabled) quillRef.current?.disable();
    else quillRef.current?.enable();
  }, [disabled]);

  return (
    <BaseRteHolder
      id={id}
      data-qa-id={qaId}
      ref={holderRef}
      $isFullscreen={isFullscreen}
      style={
        {
          [baseRteQuillEditorMinHeightVar]: `${minHeight}px`,
        } as CSSProperties
      }
    >
      <BaseRteQuillHolder $isFullscreen={isFullscreen} $hasError={hasError}>
        <QuillWrapperLoadable
          modules={modules}
          formats={formats}
          placeholder={placeholder ?? ''}
          initialValue={initialValue || ''}
          onReady={onReady}
          onChange={({ quill }) => {
            onChange?.(quill.getSemanticHTML());
          }}
          onPaste={({ event }) => {
            onPasteText?.(event.clipboardData?.getData('text/plain') ?? '');
          }}
        />
      </BaseRteQuillHolder>

      {intro && (
        <BaseRteIntroOutro
          holderRef={holderRef}
          // Approximately toolbar + margin
          top={48}
          offesetVariable={baseRteIntroHeightVar}
          qaId="intro"
        >
          {intro}
        </BaseRteIntroOutro>
      )}

      {outro && (
        <BaseRteIntroOutro
          holderRef={holderRef}
          qaId="outro"
          bottom={0}
          right={40}
          offesetVariable={baseRteOutroHeightVar}
        >
          {outro}
        </BaseRteIntroOutro>
      )}

      {warning && (
        <BaseRteWarningHolder>
          {warning.text}

          <BaseRteDismissWarningButton onClick={warning.onDismiss}>
            {i18n('Dismiss')}
          </BaseRteDismissWarningButton>
        </BaseRteWarningHolder>
      )}
    </BaseRteHolder>
  );
}
